import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/**
 * PageHeader — standardized page header used across all agent pages.
 *
 * Props:
 *   title       - Page heading (h1)
 *   subtitle    - Optional description
 *   actions     - Optional right-aligned content (buttons, badges)
 *   breadcrumbs - Optional array of { label, href? } for navigation context
 *   className   - Optional extra classes
 *
 * Usage:
 *   <PageHeader
 *     breadcrumbs={[{ label: 'Dashboard', href: '/agent/dashboard' }, { label: 'Trips' }]}
 *     title="Trip Management"
 *     subtitle="Manage trips with status tracking"
 *     actions={<button>Create Trip</button>}
 *   />
 */
export default function PageHeader({ title, subtitle, actions, breadcrumbs, className = '' }) {
  return (
    <div className={`mb-6 ${className}`}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex items-center mb-2 text-xs text-gray-500" aria-label="Breadcrumb">
          <ol className="flex items-center gap-1">
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                <li key={i} className="flex items-center gap-1">
                  {crumb.href && !isLast ? (
                    <Link
                      to={crumb.href}
                      className="transition-colors hover:text-gray-700 hover:underline"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className={isLast ? 'text-gray-700 font-medium' : ''}>
                      {crumb.label}
                    </span>
                  )}
                  {!isLast && <ChevronRight className="w-3 h-3 text-gray-400" />}
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      {/* Title row */}
      <div className="flex flex-col gap-3 justify-between sm:flex-row sm:items-center">
        <div className="min-w-0">
          <h1 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="flex flex-shrink-0 gap-2 items-center">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
